/**
 * HustleMeetLeaderboard — compact host-leaderboard slot.
 * Renders top-N HustleMeet hosts by 30-day avg star rating.
 * Falls back to an empty-state card if no ratings have been collected yet.
 */
import { useEffect, useState } from "react";
import { Star, Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function HustleMeetLeaderboard({ days = 30, limit = 10 }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/hustlemeet/leaderboard?days=${days}&limit=${limit}`)
      .then((r) => (r.ok ? r.json() : { hosts: [] }))
      .then((j) => { if (alive) setRows(j.hosts || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [days, limit]);

  if (rows === null) return null;

  return (
    <section
      data-testid="hm-leaderboard"
      className="rounded-3xl border-2 border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <Award className="text-amber-500" size={22} />
        <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
          Host leaderboard · last {days} days
        </p>
      </div>
      <h3 className="text-2xl md:text-3xl font-black text-stone-900 dark:text-stone-100 mb-2">
        The hosts making people come back.
      </h3>
      <p className="text-sm text-stone-600 dark:text-stone-400 mb-6">
        Ranked by real 5-star ratings from attendees who showed up.
        Minimum 3 ratings to appear.
      </p>

      {rows.length === 0 ? (
        <div
          data-testid="hm-leaderboard-empty"
          className="text-center rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-8 text-sm text-stone-500 dark:text-stone-400"
        >
          No rated hosts yet. Once attendees rate {`>=3`} events, top hosts appear here.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="hm-leaderboard-list">
          {rows.map((h, i) => (
            <li
              key={h.host_id}
              data-testid={`hm-leaderboard-row-${i + 1}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg font-black text-amber-500 w-6 text-right">
                  {i + 1}
                </span>
                <div>
                  <div className="font-bold text-stone-900 dark:text-stone-100">
                    {h.display_name || h.host_id}
                  </div>
                  <div className="text-[11px] text-stone-500">{h.rating_count} ratings</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Star size={16} className="text-amber-500 fill-amber-500" />
                <strong className="text-stone-900 dark:text-stone-100">
                  {h.avg_stars.toFixed(2)}
                </strong>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
